import { USER_TYPE, useAuthStoreShallow } from "@/features/authentication";
import { type ReactNode } from "react";
import { Navigate, Outlet } from "react-router-dom";

interface RoleGuardProps {
  /**
   * USER_TYPE values that are blocked from this route. Prefer the canonical
   * constants (e.g. USER_TYPE.CREATOR_LITE); the legacy "creatorLite" form is
   * also accepted, since both sides are normalized before comparison.
   */
  notAllowed: readonly string[];
  /** Where to send a blocked user. Defaults to the always-available dashboard. */
  redirectTo?: string;
  children?: ReactNode;
}

// Legacy "creatorLite" and current "creator lite" are the same user type.
// Normalize both so a single USER_TYPE.CREATOR_LITE entry blocks both forms.
const normalizeUserType = (userType: string): string =>
  userType === USER_TYPE.CREATOR_LITE_OLD ? USER_TYPE.CREATOR_LITE : userType;

const RoleGuard = ({
  notAllowed,
  redirectTo = "/pages/home",
  children,
}: RoleGuardProps) => {
  const { claims, userType } = useAuthStoreShallow((s) => ({
    claims: s.claims,
    userType: s.userType,
  }));

  // Claims not resolved yet — don't redirect a permitted user prematurely.
  // Also covers the brief logout window where claims are nulled before the
  // auth state finishes tearing down: render nothing rather than flash content.
  if (claims === null) return null;

  const normalizedNotAllowed = notAllowed.map(normalizeUserType);
  if (normalizedNotAllowed.includes(normalizeUserType(userType))) {
    return <Navigate to={redirectTo} replace />;
  }

  return children ?? <Outlet />;
};

export default RoleGuard;
